import { useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useTitle } from 'react-use';

import PageContentWrapper from '@/components/templates/PageContentWrapper';
import { SettingsContent } from '@/components/templates/SettingsContent/SettingsContent';
import { useDeskId } from '@/hooks/useDeskId';
import useDesks from '@/hooks/useDesks';
import { selectAccountSlug } from '@/redux/session/selectors';
import useSegments from '@/segment/useSegments';
import { PageName } from '@/shared/types/segment';

import DeleteDeskFieldset from './fieldsets/DeleteDeskFieldset';
import DeskIdFieldset from './fieldsets/DeskIdFieldset';
import HumanChatFieldset from './fieldsets/HumanChatFieldset';
import InformationFieldset from './fieldsets/InformationFieldset';
import KeepAliveFieldset from './fieldsets/KeepAliveFieldset';

const DeskSettings = () => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const navigate = useNavigate();
  const deskId = useDeskId();
  const { desk, deleteStatus } = useDesks(deskId);
  useTitle(t('pages.brain_settings', { 0: desk?.name ?? '-' }));

  useSegments(PageName.DESK_SETTINGS);

  useEffect(() => {
    if (deleteStatus === 'success') {
      navigate(`/${slug}/desks`);
    }
  }, [slug, navigate, deleteStatus]);

  return (
    <PageContentWrapper>
      {/* max-w-xl matches the Fieldset atom cap; min-w-128 keeps fieldsets from squishing below 512px */}
      <SettingsContent className="max-w-xl min-w-128">
        <InformationFieldset desk={desk} />
        <HumanChatFieldset key={deskId} desk={desk} />
        <KeepAliveFieldset desk={desk} />
        <DeskIdFieldset desk={desk} />
        <DeleteDeskFieldset desk={desk} />
      </SettingsContent>
    </PageContentWrapper>
  );
};

export default DeskSettings;
